/**
 * Themes. Built-in themes ship with the page; user themes are CSS files the
 * user imports, kept in IndexedDB. The desktop picked user themes up from its
 * config folder without a restart; here an import, or a removal in another
 * tab, reaches every tab through a BroadcastChannel.
 */
import type { ThemeInfo } from './types'
import { db } from './db'

/** Shipped themes, in the order the Themes menu lists them. */
export const BUILTIN_THEMES: ThemeInfo[] = [
  { id: 'claude-light', name: 'Claude Light', builtin: true },
  { id: 'github', name: 'Github', builtin: true },
  { id: 'gruvbox-dark', name: 'Gruvbox Dark', builtin: true },
  { id: 'newsprint', name: 'Newsprint', builtin: true },
  { id: 'night', name: 'Night', builtin: true },
  { id: 'nord', name: 'Nord', builtin: true },
  { id: 'one-dark', name: 'One Dark', builtin: true },
  { id: 'pixyll', name: 'Pixyll', builtin: true },
  { id: 'sepia', name: 'Sepia', builtin: true },
  { id: 'whitey', name: 'Whitey', builtin: true },
]

/** Filename-safe ids only, as on the desktop. */
const THEME_ID = /^[\w.-]+$/

function titleCase(slug: string): string {
  return slug.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/** A theme id from a file name: lower case, filename-safe. */
export function themeIdFor(fileName: string): string {
  return fileName
    .replace(/\.css$/i, '')
    .toLowerCase()
    .replace(/[^\w.-]+/g, '-')
    .replace(/^[.-]+|-+$/g, '')
}

const channel =
  typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('ekram-md:themes') : null
const listeners = new Set<() => void>()
channel?.addEventListener('message', () => {
  for (const fn of listeners) fn()
})

export function onThemesChanged(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function changed(): void {
  channel?.postMessage('changed')
  for (const fn of listeners) fn()
}

export async function listThemes(): Promise<ThemeInfo[]> {
  let user: ThemeInfo[] = []
  try {
    user = (await (await db()).getAll('themes'))
      .filter((t) => !BUILTIN_THEMES.some((b) => b.id === t.id))
      .map((t) => ({ id: t.id, name: t.name, builtin: false }))
      .sort((a, b) => a.name.localeCompare(b.name))
  } catch {
    // No IndexedDB (a locked-down browser): the built-ins only.
  }
  return [...BUILTIN_THEMES, ...user]
}

export async function readTheme(id: string): Promise<string> {
  if (!THEME_ID.test(id) || id.startsWith('.')) return ''
  return (await (await db()).get('themes', id))?.css ?? ''
}

/**
 * Keeps an imported theme. Its id comes from the file name; a built-in's id is
 * refused, so an import can never replace a shipped theme.
 */
export async function importTheme(fileName: string, css: string): Promise<string> {
  const id = themeIdFor(fileName)
  if (!id || !THEME_ID.test(id)) throw new Error('That file name cannot be used as a theme name.')
  if (BUILTIN_THEMES.some((t) => t.id === id)) {
    throw new Error(`“${id}” is the name of a built-in theme. Rename the file and import it again.`)
  }
  await (await db()).put('themes', { id, name: titleCase(id), css })
  changed()
  return id
}

export async function removeTheme(id: string): Promise<void> {
  await (await db()).delete('themes', id)
  changed()
}
